import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import PageHeader from "@/components/PageHeader";
import { Scroll, Save, Info, FileText, Plus, Trash2, AlertTriangle } from "lucide-react";

const EMPTY = {
  has_will: "unknown",
  will_location: "",
  executor_name: "",
  executor_phone: "",
  executor_email: "",
  executor_relationship: "",
  notes: "",
  instructions_for_executor: "",
  physical_documents: [],
};

export default function WillExecutor() {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/will-executor");
      const d = r.data || {};
      setForm({
        has_will: d.has_will || "unknown",
        will_location: d.will_location || "",
        executor_name: d.executor_name || "",
        executor_phone: d.executor_phone || "",
        executor_email: d.executor_email || "",
        executor_relationship: d.executor_relationship || "",
        notes: d.notes || "",
        instructions_for_executor: d.instructions_for_executor || "",
        physical_documents: Array.isArray(d.physical_documents) ? d.physical_documents : [],
      });
    } catch {
      /* page renders empty if request fails */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setDocRow = (idx, key, value) => {
    setForm((f) => {
      const arr = [...(f.physical_documents || [])];
      arr[idx] = { ...arr[idx], [key]: value };
      return { ...f, physical_documents: arr };
    });
  };

  const addDocRow = () => {
    setForm((f) => ({
      ...f,
      physical_documents: [...(f.physical_documents || []), { title: "", location: "", notes: "" }],
    }));
  };

  const removeDocRow = (idx) => {
    setForm((f) => ({
      ...f,
      physical_documents: (f.physical_documents || []).filter((_, i) => i !== idx),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/will-executor", form);
      toast.success(t("saved"));
    } catch (err) {
      // 403 plan_required → handled globally by paywall interceptor
      if (err?.response?.status !== 403) {
        toast.error(err?.response?.data?.detail || "Error");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="will-executor-loading" className="text-faded">
        {t("loading") || "…"}
      </div>
    );
  }

  return (
    <div data-testid="will-executor-page">
      <PageHeader
        title={t("will_title")}
        subtitle={t("will_subtitle")}
      />

      <div className="bg-gold/5 border border-gold-subtle rounded-md p-4 mb-6 flex gap-3 text-sm text-muted">
        <Info className="w-5 h-5 shrink-0 text-gold mt-0.5" />
        <span>{t("will_info_banner")}</span>
      </div>

      {/* Warning when the user explicitly says NO will exists */}
      {form.has_will === "no" && (
        <div
          className="bg-amber-500/5 border border-amber-500/40 rounded-md p-4 mb-6 flex gap-3 text-sm text-amber-200/90"
          data-testid="will-no-will-warning"
        >
          <AlertTriangle className="w-5 h-5 shrink-0 text-amber-400 mt-0.5" />
          <span>{t("will_no_will_warning")}</span>
        </div>
      )}

      <form onSubmit={save} className="space-y-5 max-w-3xl">
        {/* Existence */}
        <div className="glass-card rounded-md p-5">
          <div className="font-display text-gold-light text-lg mb-3 flex items-center gap-2">
            <Scroll className="w-4 h-4 text-gold" /> {t("will_exists_q")}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: "yes", k: "will_exists_yes" },
              { v: "no", k: "will_exists_no" },
              { v: "unknown", k: "will_exists_unknown" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setField("has_will", opt.v)}
                className={`px-3 py-2.5 rounded-md text-sm border transition ${
                  form.has_will === opt.v
                    ? "bg-gold/10 border-gold text-gold-light"
                    : "border-gold-subtle text-muted hover:text-gold-light hover:border-gold"
                }`}
                data-testid={`will-exists-${opt.v}`}
              >
                {t(opt.k)}
              </button>
            ))}
          </div>
        </div>

        {/* Will location — only visible when has_will === yes */}
        {form.has_will === "yes" && (
          <div className="glass-card rounded-md p-5 space-y-4">
            <div>
              <label className="font-label text-faded block mb-2">{t("will_location_label")}</label>
              <textarea
                rows={2}
                className="input-field"
                value={form.will_location}
                onChange={(e) => setField("will_location", e.target.value)}
                placeholder={t("will_location_ph")}
                data-testid="will-location"
              />
              <p className="text-[11px] text-faded mt-1.5">{t("will_location_hint")}</p>
            </div>
          </div>
        )}

        {/* Executor section */}
        <div className="glass-card rounded-md p-5 space-y-4">
          <div className="font-display text-gold-light text-lg">{t("will_executor_section")}</div>
          <p className="text-xs text-muted -mt-2">{t("will_executor_section_hint")}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-label text-faded block mb-2">{t("will_executor_name")}</label>
              <input
                className="input-field"
                value={form.executor_name}
                onChange={(e) => setField("executor_name", e.target.value)}
                data-testid="will-executor-name"
              />
            </div>
            <div>
              <label className="font-label text-faded block mb-2">{t("will_executor_relationship")}</label>
              <input
                className="input-field"
                value={form.executor_relationship}
                onChange={(e) => setField("executor_relationship", e.target.value)}
                placeholder={t("will_executor_relationship_ph")}
                data-testid="will-executor-relationship"
              />
            </div>
            <div>
              <label className="font-label text-faded block mb-2">{t("will_executor_phone")}</label>
              <input
                className="input-field"
                type="tel"
                value={form.executor_phone}
                onChange={(e) => setField("executor_phone", e.target.value)}
                data-testid="will-executor-phone"
              />
            </div>
            <div>
              <label className="font-label text-faded block mb-2">{t("will_executor_email")}</label>
              <input
                className="input-field"
                type="email"
                value={form.executor_email}
                onChange={(e) => setField("executor_email", e.target.value)}
                data-testid="will-executor-email"
              />
            </div>
          </div>

          <div>
            <label className="font-label text-faded block mb-2">{t("will_notes")}</label>
            <textarea
              rows={3}
              className="input-field"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder={t("will_notes_ph")}
              data-testid="will-notes"
            />
          </div>
        </div>

        {/* Physical documents — DNI, deeds, paper insurance, etc. */}
        <div className="glass-card rounded-md p-5 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="font-display text-gold-light text-lg flex items-center gap-2">
                <FileText className="w-4 h-4 text-gold" /> {t("will_phys_title")}
              </div>
              <p className="text-xs text-muted mt-1">{t("will_phys_hint")}</p>
            </div>
            <button
              type="button"
              onClick={addDocRow}
              className="text-xs px-3 py-1.5 rounded-md border border-gold-subtle text-gold-light hover:bg-gold/10 transition flex items-center gap-1.5 shrink-0"
              data-testid="will-phys-add"
            >
              <Plus className="w-3.5 h-3.5" /> {t("will_phys_add")}
            </button>
          </div>

          {(form.physical_documents || []).length === 0 ? (
            <p className="text-faded text-sm italic">{t("will_phys_empty")}</p>
          ) : (
            <div className="space-y-3" data-testid="will-phys-list">
              {form.physical_documents.map((row, idx) => (
                <div
                  key={idx}
                  className="border border-gold-subtle rounded-md p-3 space-y-2 relative bg-white/[0.02]"
                  data-testid={`will-phys-row-${idx}`}
                >
                  <button
                    type="button"
                    onClick={() => removeDocRow(idx)}
                    className="absolute top-2 right-2 text-faded hover:text-red-400 transition"
                    aria-label={t("delete")}
                    data-testid={`will-phys-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <div>
                    <label className="font-label text-faded text-[11px] block mb-1">{t("will_phys_doc_label")}</label>
                    <input
                      className="input-field"
                      value={row.title || ""}
                      onChange={(e) => setDocRow(idx, "title", e.target.value)}
                      placeholder={t("will_phys_doc_ph")}
                      data-testid={`will-phys-title-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="font-label text-faded text-[11px] block mb-1">{t("will_phys_loc_label")}</label>
                    <input
                      className="input-field"
                      value={row.location || ""}
                      onChange={(e) => setDocRow(idx, "location", e.target.value)}
                      placeholder={t("will_phys_loc_ph")}
                      data-testid={`will-phys-location-${idx}`}
                    />
                  </div>
                  <div>
                    <label className="font-label text-faded text-[11px] block mb-1">{t("will_phys_notes_label")}</label>
                    <textarea
                      rows={2}
                      className="input-field"
                      value={row.notes || ""}
                      onChange={(e) => setDocRow(idx, "notes", e.target.value)}
                      placeholder={t("will_phys_notes_ph")}
                      data-testid={`will-phys-notes-${idx}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions for executor — also surfaced inside ICE */}
        <div className="glass-card rounded-md p-5 space-y-3">
          <div className="font-display text-gold-light text-lg">{t("will_instructions_title")}</div>
          <p className="text-xs text-muted">{t("will_instructions_hint")}</p>
          <textarea
            rows={5}
            className="input-field"
            value={form.instructions_for_executor}
            onChange={(e) => setField("instructions_for_executor", e.target.value)}
            placeholder={t("will_instructions_ph")}
            data-testid="will-instructions"
          />
        </div>

        <div className="flex justify-end pt-2">
          <button
            type="submit"
            className="btn-gold"
            disabled={saving}
            data-testid="will-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? (t("saving") || "…") : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}
